import { useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import api from "../lib/api";

const WEEKDAY_LABELS = [
    { id: "mon", label: "Segunda" },
    { id: "tue", label: "Terça" },
    { id: "wed", label: "Quarta" },
    { id: "thu", label: "Quinta" },
    { id: "fri", label: "Sexta" },
    { id: "sat", label: "Sábado" },
    { id: "sun", label: "Domingo" },
];

const DEFAULT_HOURS = {
    mon: { open: true, from: "09:00", to: "20:00" },
    tue: { open: true, from: "09:00", to: "20:00" },
    wed: { open: true, from: "09:00", to: "20:00" },
    thu: { open: true, from: "09:00", to: "20:00" },
    fri: { open: true, from: "09:00", to: "21:00" },
    sat: { open: true, from: "09:00", to: "18:00" },
    sun: { open: false, from: "10:00", to: "16:00" },
};

const ESTABLISHMENT_TYPES = [
    "Salão de Beleza",
    "Barbearia",
    "Studio de Estética",
    "Salão Misto",
    "Outro",
];

const STEPS = [
    { id: 1, title: "Identidade", icon: "fa-store", desc: "Como seu salão será reconhecido na plataforma" },
    { id: 2, title: "Contato", icon: "fa-address-card", desc: "Tipo de negócio e como seus clientes te encontram" },
    { id: 3, title: "Endereço", icon: "fa-location-dot", desc: "Onde seus clientes te encontram" },
    { id: 4, title: "Horários", icon: "fa-clock", desc: "Define a grade da Agenda" },
    { id: 5, title: "Pronto!", icon: "fa-flag-checkered", desc: "Confira e comece a usar" },
];

const formatPhoneBR = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const OnboardingWizard = () => {
    const { brand, updateBrand } = useSalon();
    const [step, setStep] = useState(1);
    const [identity, setIdentity] = useState({
        shopId: brand.shopId || "",
        shopName: brand.shopName || "",
    });
    const [contact, setContact] = useState({
        establishmentType: brand.establishmentType || "",
        phone: brand.phone ? formatPhoneBR(brand.phone) : "",
        instagram: brand.instagram || "",
        coverUrl: brand.coverUrl || "",
    });
    const [address, setAddress] = useState(brand.salonAddress || {
        zip: "", street: "", number: "", complement: "", district: "", city: "", state: "",
    });
    const [hours, setHours] = useState(brand.salonHours || DEFAULT_HOURS);
    const [saving, setSaving] = useState(false);

    const setAddr = (k, v) => setAddress((a) => ({ ...a, [k]: v }));
    const setHour = (dayId, patch) => setHours((h) => ({ ...h, [dayId]: { ...h[dayId], ...patch } }));

    const copyMondayToAll = () => {
        const ref = hours.mon;
        const next = {};
        for (const d of WEEKDAY_LABELS) next[d.id] = { ...ref };
        setHours(next);
        toast.success("Horário replicado para todos os dias");
    };

    const handleCoverFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Arquivo maior que 2 MB.");
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => setContact((c) => ({ ...c, coverUrl: ev.target.result }));
        reader.readAsDataURL(file);
    };

    const handleNext = async () => {
        if (step === 1) {
            if (!identity.shopId.trim()) { toast.error("Informe o identificador da loja."); return; }
            if (!identity.shopName.trim()) { toast.error("Informe o nome do salão."); return; }
            const slug = identity.shopId.toLowerCase().replace(/[^a-z0-9-_]/g, "");
            if (slug !== identity.shopId) setIdentity((i) => ({ ...i, shopId: slug }));
        }
        if (step === 2) {
            if (!contact.establishmentType) { toast.error("Selecione o tipo de estabelecimento."); return; }
            const digits = contact.phone.replace(/\D/g, "");
            if (digits.length < 10) { toast.error("Informe um telefone de contato válido."); return; }
        }
        if (step === 4) {
            for (const d of WEEKDAY_LABELS) {
                const h = hours[d.id];
                if (h.open && h.from >= h.to) {
                    toast.error(`${d.label}: horário inválido (abertura ≥ fechamento)`);
                    return;
                }
            }
            await handleSaveData();
            return; // handleSaveData advances to step 5 on success
        }
        if (step < 5) setStep(step + 1);
    };

    const handleBack = () => { if (step > 1) setStep(step - 1); };

    // Persiste tudo (identidade, contato, endereço, horários) ao sair do step 4.
    // Ainda NÃO marca onboarding_completed — isso só acontece quando o usuário
    // confirma no step 5, pra não desmontar o wizard antes da tela de resumo.
    const handleSaveData = async () => {
        if (saving) return; // guard contra double-click
        const slug = identity.shopId.toLowerCase().replace(/[^a-z0-9-_]/g, "");
        const phoneDigits = contact.phone.replace(/\D/g, "");
        setSaving(true);
        try {
            await api.patch("/tenant/settings", {
                shop_name: identity.shopName.trim() || undefined,
                brand: {
                    establishmentType: contact.establishmentType,
                    phone: phoneDigits,
                    instagram: contact.instagram.trim(),
                    coverUrl: contact.coverUrl,
                    salonAddress: address,
                    salonHours: hours,
                },
            });
            updateBrand({
                shopId: slug,
                shopName: identity.shopName.trim(),
                establishmentType: contact.establishmentType,
                phone: phoneDigits,
                instagram: contact.instagram.trim(),
                coverUrl: contact.coverUrl,
                salonAddress: address,
                salonHours: hours,
            });
            setStep(5);
        } catch (e) {
            toast.error("Não conseguimos salvar", {
                description: e?.message || "Tente novamente em instantes.",
            });
            // Wizard permanece montado no step atual — nada a fazer aqui.
        } finally {
            setSaving(false);
        }
    };

    // Chamado só pelo botão do step 5 — fecha o wizard de fato.
    const handleComplete = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await api.patch("/tenant/settings", { onboarding_completed: true });
            updateBrand({ onboardingComplete: true });
            toast.success("Seu salão está configurado!");
        } catch (e) {
            toast.error("Não conseguimos concluir", {
                description: e?.message || "Tente novamente em instantes.",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleSkip = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await api.patch("/tenant/settings", { onboarding_completed: true });
            updateBrand({ onboardingComplete: true });
            toast.info("Você pode configurar depois", {
                description: "Configurações → Configurações do Salão",
            });
        } catch (e) {
            toast.error("Não conseguimos salvar", {
                description: e?.message || "Tente novamente em instantes.",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="onboarding-wizard"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
        >
            <div className="rounded-2xl border border-amber-500/30 bg-slate-950 max-w-3xl w-full shadow-[0_0_80px_-20px_rgba(212,175,55,0.5)] max-h-[92vh] flex flex-col">
                {/* Header */}
                <header className="px-7 pt-6 pb-4 border-b border-amber-500/15">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center text-amber-300">
                                <i className="fa-solid fa-rocket"></i>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/70 font-bold">
                                    Configuração Inicial · Passo {step} de 5
                                </div>
                                <h2 className="salon-heading text-2xl text-white mt-0.5">
                                    Vamos preparar seu salão.
                                </h2>
                            </div>
                        </div>
                        {step < 5 && (
                            <button
                                data-testid="onboarding-skip"
                                onClick={handleSkip}
                                disabled={saving}
                                className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors px-3 py-1 rounded-md hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Pular por agora
                            </button>
                        )}
                    </div>

                    {/* Stepper */}
                    <div className="flex items-center gap-2 mt-5">
                        {STEPS.map((s, idx) => {
                            const done = step > s.id;
                            const active = step === s.id;
                            return (
                                <div key={s.id} className="flex-1 flex items-center gap-2">
                                    <div
                                        data-testid={`onboarding-step-indicator-${s.id}`}
                                        className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all flex-shrink-0 ${
                                            done
                                                ? "bg-emerald-500/20 border-emerald-400 text-emerald-300"
                                                : active
                                                    ? "bg-amber-500 border-amber-400 text-slate-950"
                                                    : "bg-slate-900 border-white/10 text-slate-500"
                                        }`}
                                    >
                                        {done ? <i className="fa-solid fa-check"></i> : s.id}
                                    </div>
                                    <div className={`flex-1 min-w-0 hidden sm:block ${active ? "" : "opacity-60"}`}>
                                        <div className={`text-xs font-bold uppercase tracking-wider ${active ? "text-amber-200" : "text-slate-400"}`}>
                                            {s.title}
                                        </div>
                                    </div>
                                    {idx < STEPS.length - 1 && (
                                        <div className={`h-px flex-1 ${done ? "bg-emerald-400/50" : "bg-white/10"}`}></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </header>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-7 py-6">
                    {step === 1 && (
                        <div data-testid="onboarding-step-1" className="space-y-5">
                            <div>
                                <p className="text-sm text-slate-300 leading-relaxed">
                                    Bem-vindo ao Kinng.app. Vamos definir o nome e o
                                    identificador exclusivo do seu salão — eles aparecem
                                    no app, nas notificações enviadas aos clientes e na
                                    URL do seu painel.
                                </p>
                            </div>
                            <Field label="Nome do Salão">
                                <input
                                    data-testid="onb-shop-name"
                                    autoFocus
                                    value={identity.shopName}
                                    onChange={(e) => setIdentity((i) => ({ ...i, shopName: e.target.value }))}
                                    placeholder="Ex.: Salão Vintage Club"
                                    className="w-full min-h-[48px] rounded-xl bg-black/60 border border-amber-500/25 px-4 text-base text-white outline-none focus:border-amber-400"
                                />
                            </Field>
                            <Field label="Identificador da Loja (URL única)">
                                <div className="flex items-center rounded-xl border border-amber-500/25 bg-black/60 focus-within:border-amber-400 transition-colors">
                                    <span className="px-3 text-slate-500 text-sm font-mono select-none">kinng.app/</span>
                                    <input
                                        data-testid="onb-shop-id"
                                        value={identity.shopId}
                                        onChange={(e) => setIdentity((i) => ({ ...i, shopId: e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, "") }))}
                                        placeholder="salaovintage"
                                        className="flex-1 min-h-[48px] bg-transparent pr-4 text-white outline-none placeholder:text-slate-600 font-mono text-sm"
                                    />
                                </div>
                                <p className="text-[11px] text-slate-500 mt-1.5">
                                    <i className="fa-solid fa-circle-info mr-1"></i>
                                    Use somente letras minúsculas, números, hífen ou underline.
                                </p>
                            </Field>
                        </div>
                    )}

                    {step === 2 && (
                        <div data-testid="onboarding-step-2" className="space-y-4">
                            <p className="text-sm text-slate-300 leading-relaxed">
                                Conte um pouco sobre o seu negócio e como os clientes
                                podem falar com você — isso ajuda a personalizar sua
                                experiência no Kinng.app.
                            </p>
                            <div className="grid md:grid-cols-2 gap-3">
                                <Field label="Tipo de Estabelecimento">
                                    <select
                                        data-testid="onb-establishment-type"
                                        value={contact.establishmentType}
                                        onChange={(e) => setContact((c) => ({ ...c, establishmentType: e.target.value }))}
                                        className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/25 px-3.5 text-sm text-white outline-none focus:border-amber-400"
                                    >
                                        <option value="">Selecionar...</option>
                                        {ESTABLISHMENT_TYPES.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Telefone de Contato">
                                    <input
                                        data-testid="onb-phone"
                                        value={contact.phone}
                                        onChange={(e) => setContact((c) => ({ ...c, phone: formatPhoneBR(e.target.value) }))}
                                        placeholder="(00) 00000-0000"
                                        inputMode="numeric"
                                        className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/25 px-3.5 text-sm text-white outline-none focus:border-amber-400 font-mono"
                                    />
                                </Field>
                                <Field label="Instagram (opcional)">
                                    <input
                                        data-testid="onb-instagram"
                                        value={contact.instagram}
                                        onChange={(e) => setContact((c) => ({ ...c, instagram: e.target.value }))}
                                        placeholder="@seusalão"
                                        className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/25 px-3.5 text-sm text-white outline-none focus:border-amber-400"
                                    />
                                </Field>
                            </div>
                            <Field label="Foto de Capa do Salão (opcional)">
                                <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-slate-950/50 p-3.5">
                                    <div className="h-16 w-24 rounded-lg border border-amber-500/25 bg-black flex items-center justify-center overflow-hidden flex-shrink-0">
                                        {contact.coverUrl ? (
                                            <img data-testid="onb-cover-preview" src={contact.coverUrl} alt="Capa" className="h-full w-full object-cover" />
                                        ) : (
                                            <i className="fa-solid fa-image text-slate-600 text-xl"></i>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[11px] text-slate-500 leading-relaxed mb-2">PNG ou JPG, até 2 MB.</p>
                                        <div className="flex items-center gap-2">
                                            <label
                                                data-testid="onb-cover-upload-label"
                                                className="min-h-[36px] px-3.5 rounded-lg border border-amber-500/40 hover:border-amber-400 bg-amber-500/5 hover:bg-amber-500/10 text-amber-200 text-[10px] uppercase tracking-wider font-bold cursor-pointer inline-flex items-center gap-2 transition-all"
                                            >
                                                <i className="fa-solid fa-upload"></i>
                                                {contact.coverUrl ? "Trocar foto" : "Enviar foto"}
                                                <input
                                                    data-testid="onb-cover-file"
                                                    type="file"
                                                    accept="image/png,image/jpeg,image/webp"
                                                    className="hidden"
                                                    onChange={handleCoverFile}
                                                />
                                            </label>
                                            {contact.coverUrl && (
                                                <button
                                                    type="button"
                                                    data-testid="onb-cover-remove"
                                                    onClick={() => setContact((c) => ({ ...c, coverUrl: "" }))}
                                                    className="min-h-[36px] px-3 rounded-lg border border-white/10 hover:border-red-500/40 text-slate-400 hover:text-red-300 text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-2 transition-all"
                                                >
                                                    <i className="fa-solid fa-trash"></i>
                                                    Remover
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Field>
                        </div>
                    )}

                    {step === 3 && (
                        <div data-testid="onboarding-step-3" className="space-y-4">
                            <p className="text-sm text-slate-300 leading-relaxed">
                                Esse endereço aparecerá nas comunicações com seus clientes,
                                recibos digitais e no link público de agendamento.
                            </p>
                            <div className="grid md:grid-cols-6 gap-3">
                                <Field label="CEP" className="md:col-span-2">
                                    <input
                                        data-testid="onb-zip"
                                        value={address.zip}
                                        onChange={(e) => setAddr("zip", e.target.value.replace(/\D/g, "").slice(0, 8))}
                                        placeholder="00000-000"
                                        className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/25 px-3.5 text-sm text-white outline-none focus:border-amber-400 font-mono"
                                    />
                                </Field>
                                <Field label="Estado" className="md:col-span-1">
                                    <input
                                        data-testid="onb-state"
                                        value={address.state}
                                        onChange={(e) => setAddr("state", e.target.value.toUpperCase().slice(0, 2))}
                                        placeholder="SP"
                                        className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/25 px-3.5 text-sm text-white outline-none focus:border-amber-400 uppercase"
                                    />
                                </Field>
                                <Field label="Cidade" className="md:col-span-3">
                                    <input
                                        data-testid="onb-city"
                                        value={address.city}
                                        onChange={(e) => setAddr("city", e.target.value)}
                                        placeholder="Ex.: São Paulo"
                                        className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/25 px-3.5 text-sm text-white outline-none focus:border-amber-400"
                                    />
                                </Field>
                                <Field label="Rua / Avenida" className="md:col-span-4">
                                    <input
                                        data-testid="onb-street"
                                        value={address.street}
                                        onChange={(e) => setAddr("street", e.target.value)}
                                        placeholder="Ex.: Av. Paulista"
                                        className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/25 px-3.5 text-sm text-white outline-none focus:border-amber-400"
                                    />
                                </Field>
                                <Field label="Número" className="md:col-span-1">
                                    <input
                                        data-testid="onb-number"
                                        value={address.number}
                                        onChange={(e) => setAddr("number", e.target.value)}
                                        placeholder="123"
                                        className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/25 px-3.5 text-sm text-white outline-none focus:border-amber-400"
                                    />
                                </Field>
                                <Field label="Bairro" className="md:col-span-3">
                                    <input
                                        data-testid="onb-district"
                                        value={address.district}
                                        onChange={(e) => setAddr("district", e.target.value)}
                                        placeholder="Bela Vista"
                                        className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/25 px-3.5 text-sm text-white outline-none focus:border-amber-400"
                                    />
                                </Field>
                                <Field label="Complemento (opcional)" className="md:col-span-3">
                                    <input
                                        data-testid="onb-complement"
                                        value={address.complement}
                                        onChange={(e) => setAddr("complement", e.target.value)}
                                        placeholder="Sala 101 · Bloco B"
                                        className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/25 px-3.5 text-sm text-white outline-none focus:border-amber-400"
                                    />
                                </Field>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div data-testid="onboarding-step-4" className="space-y-3">
                            <div className="flex items-baseline justify-between">
                                <p className="text-sm text-slate-300 leading-relaxed flex-1">
                                    Marque os dias e horários de atendimento. Isso define a grade da Agenda e
                                    bloqueia agendamentos fora do expediente.
                                </p>
                                <button
                                    data-testid="onb-copy-monday"
                                    onClick={copyMondayToAll}
                                    className="text-[10px] uppercase tracking-wider font-bold text-amber-300 hover:text-amber-200 transition-colors px-3 py-1.5 rounded-lg border border-amber-500/20 hover:border-amber-500/50 ml-3 shrink-0"
                                >
                                    <i className="fa-solid fa-copy mr-1.5"></i>Copiar Seg p/ todos
                                </button>
                            </div>
                            <div className="space-y-2">
                                {WEEKDAY_LABELS.map((d) => {
                                    const h = hours[d.id];
                                    return (
                                        <div
                                            key={d.id}
                                            data-testid={`onb-hours-${d.id}`}
                                            className={`grid grid-cols-12 gap-3 items-center rounded-xl border px-3.5 py-2.5 transition-colors ${
                                                h.open ? "border-amber-500/20 bg-black/40" : "border-white/5 bg-slate-900/30"
                                            }`}
                                        >
                                            <div className="col-span-4 flex items-center gap-2.5">
                                                <button
                                                    type="button"
                                                    role="switch"
                                                    aria-checked={h.open}
                                                    data-testid={`onb-toggle-${d.id}`}
                                                    onClick={() => setHour(d.id, { open: !h.open })}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                                                        h.open ? "bg-emerald-500" : "bg-slate-700"
                                                    }`}
                                                >
                                                    <span
                                                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${
                                                            h.open ? "translate-x-6" : "translate-x-1"
                                                        }`}
                                                    />
                                                </button>
                                                <span className={`text-sm font-semibold ${h.open ? "text-white" : "text-slate-500"}`}>
                                                    {d.label}
                                                </span>
                                            </div>
                                            {h.open ? (
                                                <>
                                                    <div className="col-span-4 flex items-center gap-1.5">
                                                        <span className="text-[10px] uppercase tracking-wider text-amber-200/60">De</span>
                                                        <input
                                                            type="time"
                                                            data-testid={`onb-from-${d.id}`}
                                                            value={h.from}
                                                            onChange={(e) => setHour(d.id, { from: e.target.value })}
                                                            className="flex-1 min-h-[36px] rounded-lg bg-black/60 border border-amber-500/20 px-2.5 text-sm text-white outline-none focus:border-amber-400 font-mono"
                                                        />
                                                    </div>
                                                    <div className="col-span-4 flex items-center gap-1.5">
                                                        <span className="text-[10px] uppercase tracking-wider text-amber-200/60">Até</span>
                                                        <input
                                                            type="time"
                                                            data-testid={`onb-to-${d.id}`}
                                                            value={h.to}
                                                            onChange={(e) => setHour(d.id, { to: e.target.value })}
                                                            className="flex-1 min-h-[36px] rounded-lg bg-black/60 border border-amber-500/20 px-2.5 text-sm text-white outline-none focus:border-amber-400 font-mono"
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="col-span-8 text-xs text-slate-500 italic">
                                                    <i className="fa-solid fa-door-closed mr-2"></i>Fechado neste dia
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 5 && (
                        <div data-testid="onboarding-step-5" className="space-y-5">
                            <div className="flex flex-col items-center text-center gap-3 py-2">
                                <div className="h-16 w-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-300 text-2xl">
                                    <i className="fa-solid fa-circle-check"></i>
                                </div>
                                <h3 className="salon-heading text-xl text-white">Seu salão está configurado!</h3>
                                <p className="text-sm text-slate-400 max-w-md">
                                    Confira o resumo abaixo. Você pode ajustar qualquer detalhe depois em Configurações.
                                </p>
                            </div>

                            <div className="rounded-xl border border-white/10 bg-black/30 divide-y divide-white/5">
                                <SummaryRow icon="fa-store" label="Salão" value={`${identity.shopName} · kinng.app/${identity.shopId}`} />
                                <SummaryRow icon="fa-tag" label="Tipo" value={contact.establishmentType} />
                                <SummaryRow icon="fa-phone" label="Telefone" value={contact.phone} />
                                {contact.instagram && <SummaryRow icon="fa-brands fa-instagram" label="Instagram" value={contact.instagram} />}
                                <SummaryRow
                                    icon="fa-location-dot"
                                    label="Endereço"
                                    value={[address.street, address.number, address.district, address.city, address.state].filter(Boolean).join(", ") || "—"}
                                />
                                <SummaryRow
                                    icon="fa-clock"
                                    label="Horários"
                                    value={`${WEEKDAY_LABELS.filter((d) => hours[d.id]?.open).length} dias abertos por semana`}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <footer className={`px-7 py-4 border-t border-amber-500/15 flex items-center bg-black/40 rounded-b-2xl ${step === 5 ? "justify-center" : "justify-between"}`}>
                    {step === 5 ? (
                        <button
                            data-testid="onboarding-finish"
                            onClick={handleComplete}
                            disabled={saving}
                            className="min-h-[48px] px-8 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-[11px] uppercase tracking-[0.15em] font-bold transition-all shadow-lg shadow-amber-500/30 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <>
                                    <i className="fa-solid fa-spinner fa-spin"></i>
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    Acessar meu painel
                                    <i className="fa-solid fa-arrow-right"></i>
                                </>
                            )}
                        </button>
                    ) : (
                        <>
                            <button
                                data-testid="onboarding-back"
                                onClick={handleBack}
                                disabled={step === 1}
                                className="min-h-[44px] px-4 rounded-xl border border-white/10 hover:border-white/30 text-slate-300 text-[11px] uppercase tracking-wider font-bold disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                            >
                                <i className="fa-solid fa-arrow-left mr-2"></i>Voltar
                            </button>
                            <button
                                data-testid="onboarding-next"
                                onClick={handleNext}
                                disabled={saving}
                                className="min-h-[48px] px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-[11px] uppercase tracking-[0.15em] font-bold transition-all shadow-lg shadow-amber-500/30 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {saving ? (
                                    <>
                                        <i className="fa-solid fa-spinner fa-spin"></i>
                                        Salvando...
                                    </>
                                ) : step === 4 ? (
                                    <>
                                        <i className="fa-solid fa-check"></i>
                                        Salvar e continuar
                                    </>
                                ) : (
                                    <>
                                        Continuar
                                        <i className="fa-solid fa-arrow-right"></i>
                                    </>
                                )}
                            </button>
                        </>
                    )}
                </footer>
            </div>
        </div>
    );
};

const Field = ({ label, className = "", children }) => (
    <label className={`block ${className}`}>
        <span className="block text-[10px] uppercase tracking-[0.2em] text-amber-200/70 mb-1.5 font-bold">
            {label}
        </span>
        {children}
    </label>
);

const SummaryRow = ({ icon, label, value }) => (
    <div className="flex items-center gap-3 px-4 py-3">
        <div className="h-8 w-8 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-300 flex items-center justify-center text-xs flex-shrink-0">
            <i className={icon.startsWith("fa-brands") ? icon : `fa-solid ${icon}`}></i>
        </div>
        <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
            <div className="text-sm text-white truncate">{value}</div>
        </div>
    </div>
);

export default OnboardingWizard;
